// End of a Guess match: winner, everyone's numbers, and what the system heard for each picture.
import type { GuessResult } from "@shared/guess/api";
import { motion } from "motion/react";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link } from "wouter";
import { Avatar } from "../../components/Avatar";
import { Btn } from "../../components/Button";
import { Confetti } from "../../components/Confetti";
import { Check, History, RefreshCw, RotateCcw, SkipForward, Timer, X } from "../../components/Icon";
import { fmt, useI18n } from "../../i18n";
import { formatSeconds } from "../../lib/format";
import { play } from "../../lib/sfx";
import type { ReviewEntry } from "./controller";
import { answerText } from "./Duel";
import { useGuessDict, type GuessDict } from "./i18n";
import { PictureCard } from "./ui";

export function Results({
  result,
  status,
  verified,
  reviews,
  signedIn,
  onRematch,
  onCategory,
  onRetrySave,
  meIndex,
  actions,
}: {
  result: GuessResult;
  status: "saving" | "saved" | "save_error" | string;
  verified: boolean | null;
  reviews: ReviewEntry[];
  signedIn: boolean;
  onRematch(): void;
  onCategory(): void;
  onRetrySave(): void;
  /** online rooms: the viewer's own seat ("you"); one phone: the signed-in owner is the non-guest seat */
  meIndex?: number | null;
  /** online rooms: replaces the rematch / category buttons */
  actions?: ReactNode;
}) {
  const t = useGuessDict();
  const { lang } = useI18n();
  const solo = result.players.length === 1;
  const winner = result.winner !== null && result.winner >= 0 ? result.players[result.winner] : null;
  const ordered = useMemo(() => [...result.players].map((p, i) => ({ ...p, i })).sort((a, b) => a.rank - b.rank), [result]);
  const [fire] = useState(() => Date.now());

  useEffect(() => {
    play(solo || winner ? "win" : "reveal");
  }, [solo, winner]);

  const title = solo
    ? fmt(t.soloScore, { n: result.players[0].correct })
    : winner
      ? fmt(t.winner, { name: winner.name })
      : t.draw;

  return (
    <div className="shell py-8 sm:py-12">
      {(winner || solo) && <Confetti fire={fire} />}
      <div className="text-center">
        {winner && <Avatar index={winner.avatar} size={132} mood="happy" disc={false} className="mx-auto" />}
        <motion.h1 initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="display mt-3 text-[clamp(34px,7vw,64px)] text-guess">
          {title}
        </motion.h1>
        {result.endedBy === "bank" && <p className="mt-2 text-ink-2">{t.bankEmpty}</p>}
        <SaveLine status={status} verified={verified} t={t} onRetry={onRetrySave} />
      </div>

      {/* everyone's numbers */}
      <div className={`mx-auto mt-8 grid max-w-3xl gap-3 ${result.players.length > 2 ? "sm:grid-cols-3" : "sm:grid-cols-2"}`}>
        {ordered.map((p) => (
          <div key={p.i} className="surface flex items-center gap-4 p-4">
            <div className="relative">
              <Avatar index={p.avatar} size={64} mood={solo || p.rank === 1 ? "happy" : "sad"} disc={false} />
              {!solo && <span className="display absolute -top-1 -left-1 grid h-7 w-7 place-items-center rounded-full bg-ground text-sm">{p.rank}</span>}
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-lg font-extrabold">
                {p.name} {(meIndex === undefined ? !p.guest : meIndex === p.i) && <span className="ml-1 text-sm text-ink-3">· {t.you}</span>}
              </div>
              <dl className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-ink-2">
                <Stat label={t.statsCorrect} icon={<Check size={16} className="text-good" />} value={p.correct} />
                <Stat label={t.statsWrong} icon={<X size={16} className="text-bad" />} value={p.wrong} />
                <Stat label={t.statsSkips} icon={<SkipForward size={16} className="text-flame" />} value={p.skips} />
                <Stat label={t.statsTime} icon={<Timer size={16} />} value={formatSeconds(p.remaining)} />
              </dl>
            </div>
          </div>
        ))}
      </div>

      <div className="mx-auto mt-8 flex max-w-3xl flex-col gap-3 sm:flex-row sm:justify-center">
        {actions ?? (
          <>
            <Btn game="guess" size="lg" icon={<RotateCcw size={20} />} onClick={onRematch}>
              {t.rematch}
            </Btn>
            <Btn variant="soft" size="lg" onClick={onCategory}>
              {t.changeCategory}
            </Btn>
          </>
        )}
        {signedIn && (
          <Link href="~/guess/history" className="btn btn-ghost btn-lg">
            <History size={20} />
            {t.toHistory}
          </Link>
        )}
      </div>

      {/* what the recogniser heard: every answer that counted, and the ones it could not place */}
      {(() => {
        const shown = reviews.filter((r) => r.heard.trim() || r.verdict === "correct" || r.verdict === "wrong" || r.verdict === "skip");
        return shown.length > 0 ? <Review reviews={shown} players={result.players} t={t} lang={lang} /> : null;
      })()}
    </div>
  );
}

function Stat({ label, value, icon }: { label: string; value: number | string; icon: ReactNode }) {
  return (
    <div className="flex items-center gap-1.5" title={label}>
      <dt className="flex items-center">
        {icon}
        <span className="sr-only">{label}</span>
      </dt>
      <dd className="display text-xl text-ink">{value}</dd>
    </div>
  );
}

function SaveLine({ status, verified, t, onRetry }: { status: string; verified: boolean | null; t: GuessDict; onRetry(): void }) {
  if (status === "saving") return <p className="mt-3 text-ink-3">…</p>;
  if (status === "save_error")
    return (
      <p className="mt-3 flex items-center justify-center gap-3 text-bad">
        {t.saveFailed}
        <button type="button" className="btn btn-sm btn-soft" onClick={onRetry}>
          <RefreshCw size={16} />
        </button>
      </p>
    );
  if (status === "saved")
    return (
      <p className="mt-3 inline-flex items-center gap-2 text-ink-3">
        <Check size={16} /> {t.saved}
        {verified === false && <span className="rounded-full bg-ground-3 px-2 py-0.5 text-xs">{t.unverified}</span>}
      </p>
    );
  return null;
}

/** Every picture with what the recogniser heard and how it was judged. */
export function Review({ reviews, players, t, lang }: { reviews: ReviewEntry[]; players: { name: string }[]; t: GuessDict; lang: string }) {
  return (
    <section className="mx-auto mt-12 max-w-3xl">
      <h2 className="display text-2xl">{t.review}</h2>
      <ul className="mt-4 grid gap-2">
        {reviews.map((r) => (
          <li key={r.utteranceId} className="flex items-center gap-3 rounded-2xl bg-ground-2 p-2 pr-4">
            <div className="w-16 shrink-0">
              <PictureCard picture={r.picture} size="64px" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm text-ink-3">{players[r.player]?.name}</div>
              <div className="truncate font-bold">{r.heard ? `„${r.heard}“` : "—"}</div>
              {r.answer && <div className="truncate text-sm text-ink-2">{answerText(r.answer, lang)}</div>}
            </div>
            <VerdictChip verdict={r.verdict} t={t} />
          </li>
        ))}
      </ul>
    </section>
  );
}

export function VerdictChip({ verdict, t }: { verdict: string; t: GuessDict }) {
  const tone =
    verdict === "correct"
      ? { bg: "var(--color-good)", ink: "#06240f", icon: <Check size={14} /> }
      : verdict === "wrong"
        ? { bg: "var(--color-bad)", ink: "#fff", icon: <X size={14} /> }
        : verdict === "skip"
          ? { bg: "var(--color-flame)", ink: "#2a1200", icon: null }
          : { bg: "var(--color-ground-4)", ink: "var(--color-ink-2)", icon: null };
  return (
    <span className="inline-flex shrink-0 items-center gap-1 rounded-full px-3 py-1 text-xs font-extrabold" style={{ background: tone.bg, color: tone.ink }}>
      {tone.icon}
      {t.verdicts[verdict] ?? verdict}
    </span>
  );
}
